import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Per-send history for a contact — one row per ACTUAL EmailBison send.
 *
 * `outreach_messages` holds one row per (contact, campaign), but a Bison
 * campaign can run a multi-step sequence. Campaigns 17-20 ran initial + 3d
 * bump + 7d breakup, so 117 follow-up emails reached prospects with no CARR
 * record until the 2026-07-28 backfill. Steps 2-3 carry copy authored inside
 * Bison (`authored_by = 'bison_template'`) that exists in no CARR table —
 * the UI must label those so nobody mistakes them for CARR-generated copy.
 */

export interface ContactSend {
  id: string;
  outreach_message_id: string | null;
  contact_id: string | null;
  contact_email: string | null;
  bison_campaign_id: string | null;
  campaign_name: string | null;
  sender_name: string | null;
  message_type: string | null;
  scheduled_email_id: number;
  sequence_step_id: number | null;
  step_order: number | null;
  actual_subject: string | null;
  rendered_body: string | null;
  raw_message_id: string | null;
  sent_at: string | null;
  thread_reply: boolean;
  authored_by: "carr" | "bison_template" | "unknown";
  is_bison_authored: boolean;
  source: string;
}

/** All sends for a contact, oldest first. */
export function useContactSendTimeline(contactId: string | null) {
  return useQuery({
    queryKey: ["contact-send-timeline", contactId],
    enabled: !!contactId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("v_contact_send_timeline")
        .select("*")
        .eq("contact_id", contactId)
        .order("sent_at", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data || []) as ContactSend[];
    },
  });
}

/**
 * Sends grouped by their parent outreach_messages row, so the Comms tab can
 * render each real send under the message it belongs to.
 */
export function groupSendsByMessage(sends: ContactSend[]): Map<string, ContactSend[]> {
  const m = new Map<string, ContactSend[]>();
  for (const s of sends) {
    const key = s.outreach_message_id ?? "__unlinked__";
    if (!m.has(key)) m.set(key, []);
    m.get(key)!.push(s);
  }
  return m;
}
